// Unit helpers + conversions. Storage is always SI (Celsius, mS/cm, gallons);
// display can flip to user-preferred units via usePreferences().
//
// Why centralize these? Five temperature surfaces (pen badge, last-reading
// panel, refill detail StateCards, refill step list, grow form ranges) all
// need to flip together when the user toggles °C ↔ °F. One helper means
// one place to fix if rounding semantics change.

export type TemperatureUnit = "C" | "F";

// EC has multiple conventions in hydroponics:
//   mS/cm   — scientific (what the pen reports + how we store)
//   ppm700  — Bluelab / Truncheon scale: ppm = mS/cm × 700
// PPM 500 (Hanna) + µS/cm aren't supported here; can be added later.
export type ECUnit = "mS/cm" | "ppm700";

const PPM700_FACTOR = 700;

/** Celsius → Fahrenheit. */
export function cToF(c: number): number {
  return c * 9 / 5 + 32;
}

/** Fahrenheit → Celsius. */
export function fToC(f: number): number {
  return (f - 32) * 5 / 9;
}

/** Format a stored Celsius value in the preferred unit. Empty string when undefined. */
export function formatTemp(
  c: number | undefined | null,
  unit: TemperatureUnit,
  digits = 1,
): string {
  if (c === undefined || c === null || !Number.isFinite(c)) return "—";
  const v = unit === "F" ? cToF(c) : c;
  return v.toFixed(digits);
}

/** Convert a user-entered display value back to the storage unit (C). */
export function tempToCelsius(v: number, unit: TemperatureUnit): number {
  return unit === "F" ? fToC(v) : v;
}

/** Convert a stored C value to the display unit. */
export function tempFromCelsius(c: number, unit: TemperatureUnit): number {
  return unit === "F" ? cToF(c) : c;
}

/** Suffix label, no leading space ("°C", "°F"). */
export function tempUnitLabel(unit: TemperatureUnit): string {
  return unit === "F" ? "°F" : "°C";
}

/** Toggle to the next temperature unit. */
export function nextTempUnit(unit: TemperatureUnit): TemperatureUnit {
  return unit === "C" ? "F" : "C";
}

/** Convert a stored mS/cm EC value to the display unit. */
export function ecFromMsCm(ms: number, unit: ECUnit): number {
  return unit === "ppm700" ? ms * PPM700_FACTOR : ms;
}

/** Convert a user-entered display EC value back to the storage unit (mS/cm). */
export function ecToMsCm(v: number, unit: ECUnit): number {
  return unit === "ppm700" ? v / PPM700_FACTOR : v;
}

/** Format a stored mS/cm EC value in the preferred display unit. */
export function formatEc(
  ms: number | undefined | null,
  unit: ECUnit,
): string {
  if (ms === undefined || ms === null || !Number.isFinite(ms)) return "—";
  if (unit === "ppm700") return Math.round(ecFromMsCm(ms, unit)).toString();
  return ecFromMsCm(ms, unit).toFixed(2);
}

/** Display-unit label for EC. */
export function ecUnitLabel(unit: ECUnit): string {
  return unit === "ppm700" ? "ppm" : "EC";
}

/** Toggle to the next EC unit. */
export function nextEcUnit(unit: ECUnit): ECUnit {
  return unit === "mS/cm" ? "ppm700" : "mS/cm";
}

// Level / volume. Storage is always gallons (level_gal). Growers often measure
// depth with a dipstick in inches or centimeters; converting depth↔volume needs
// the reservoir geometry (gal_per_inch = cross-section/231; cm_per_gal is its
// inverse expressed in centimeters). Without geometry we can only show gallons.
export type LevelUnit = "gal" | "in" | "cm";

export interface ReservoirFactors {
  galPerInch?: number;
  cmPerGal?: number;
}

/** Suffix label for a level unit ("gal" | "in" | "cm"). */
export function levelUnitLabel(unit: LevelUnit): string {
  return unit;
}

/** Cycle gal → in → cm → gal (geometry-agnostic; prefer cycleLevelUnit). */
export function nextLevelUnit(unit: LevelUnit): LevelUnit {
  return unit === "gal" ? "in" : unit === "in" ? "cm" : "gal";
}

// gal_per_inch and cm_per_gal are INDEPENDENT nullable reservoir fields, so a
// conversion factor is usable only when it's present AND a positive finite
// number. A factor of 0 is both falsy and a divide-by-zero hazard (→ Infinity
// in the From direction), and NaN/Infinity are never valid geometry.
function usableFactor(x: number | undefined): boolean {
  return typeof x === "number" && Number.isFinite(x) && x > 0;
}

/** Whether ANY depth↔volume conversion is possible (in or cm geometry known). */
export function levelConvertible(f: ReservoirFactors | undefined): boolean {
  return !!f && (usableFactor(f.galPerInch) || usableFactor(f.cmPerGal));
}

/**
 * The level units this reservoir can actually represent, in display order.
 * "gal" is always available; "in" requires galPerInch > 0 and "cm" requires
 * cmPerGal > 0. The UI must only ever offer / store a unit from this list so a
 * depth is never silently persisted as gallons (~6.5x corruption of level_gal).
 */
export function levelUnitsAvailable(f: ReservoirFactors | undefined): LevelUnit[] {
  const units: LevelUnit[] = ["gal"];
  if (usableFactor(f?.galPerInch)) units.push("in");
  if (usableFactor(f?.cmPerGal)) units.push("cm");
  return units;
}

/**
 * Resolve the unit to actually display, given a persisted preference. Falls
 * back to "gal" when the preference points at a unit this reservoir can't
 * convert (e.g. pref="cm" but cmPerGal missing), so the user never silently
 * enters cm and has it stored as gallons.
 */
export function effectiveLevelUnit(
  pref: LevelUnit,
  f: ReservoirFactors | undefined,
): LevelUnit {
  return levelUnitsAvailable(f).includes(pref) ? pref : "gal";
}

/** Cycle to the next CONVERTIBLE unit, skipping any whose factor is absent. */
export function cycleLevelUnit(unit: LevelUnit, f: ReservoirFactors | undefined): LevelUnit {
  const units = levelUnitsAvailable(f);
  const i = units.indexOf(unit);
  // If the current unit isn't offerable, start the cycle from gal (index 0).
  // units always has >= 1 entry ("gal"), so this index is always in range.
  return units[(i < 0 ? 0 : i + 1) % units.length] ?? "gal";
}

/** Display-unit depth/volume → stored gallons. Identity for gal or unusable geometry. */
export function levelToGal(v: number, unit: LevelUnit, f: ReservoirFactors): number {
  if (unit === "in" && usableFactor(f.galPerInch)) return v * (f.galPerInch as number);
  if (unit === "cm" && usableFactor(f.cmPerGal)) return v / (f.cmPerGal as number);
  return v;
}

/** Stored gallons → display unit. Identity for gal or unusable geometry. */
export function levelFromGal(gal: number, unit: LevelUnit, f: ReservoirFactors): number {
  if (unit === "in" && usableFactor(f.galPerInch)) return gal / (f.galPerInch as number);
  if (unit === "cm" && usableFactor(f.cmPerGal)) return gal * (f.cmPerGal as number);
  return gal;
}
